import { createSelector } from 'reselect';
import qs from 'query-string';

import { get } from './utils/lodash';

export const getState = state => state;

export const getQueries = createSelector(
  getState,
  state => state.queries
);

export const getEntities = createSelector(
  getState,
  state => state.entities
);

export const getErrors = createSelector(
  getState,
  state => state.errors
);

export const selectLocation = createSelector(
  getState,
  state => state.router.location
);

export const selectSearch = createSelector(
  getState,
  state => get(state, 'router.location.search', '')
);

export const selectSearchObj = createSelector(
  selectSearch,
  search => {
    return qs.parse(search[0] === '?' ? search.substr(1) : search);
  }
);

export const selectPathname = createSelector(
  selectLocation,
  location => location.pathname
);

export const selectCountry = createSelector(
  selectSearchObj,
  searchObj => {
    const country = (get(searchObj, 'co', '') || get(searchObj, 'country', ''));
    const parsedCountry = Array.isArray(country) ? country[0] : country;
    return parsedCountry.toUpperCase();
  }
);

export const selectAppEnv = createSelector(() => window.AppENV)

export const selectTurnstileSiteKey = createSelector(
  getState,
  selectAppEnv,
  (state, appEnv) => {
    const recaptchaKeyFromState = get(state, 'serviceConfig.cloudpay.recaptchaKey');
    return recaptchaKeyFromState ? recaptchaKeyFromState : appEnv?.cloudpay?.recaptchaKey;
  }
);
const parseBoolean = (value) => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return false;
};
export const selectCaptchaEnabled = createSelector(
  getState,
  selectAppEnv,
  (state, appEnv) => {
    const fromState = get(state, 'serviceConfig.features.captchaEnabled');
    const fromEnv = appEnv?.features?.captchaEnabled;
    const rawValue = fromState ?? fromEnv;
    return parseBoolean(rawValue);
  }
);
const normalizeBooleanFlag = value => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return value;
};

export const selectAddOnsEnabled = createSelector(
  getState,
  selectAppEnv,
  (state, appEnv) => {
    const fromState = get(state, 'serviceConfig.features.addOnsEnabled');
    const fallback = appEnv?.features?.addOnsEnabled;
    const raw =
      fromState !== undefined && fromState !== null ? fromState : fallback;
    return normalizeBooleanFlag(raw);
  }
);

export const selectLanguage = createSelector(
  selectSearchObj,
  (searchObj) => {
    const formatNavigatorLanguage = () => {
      const [lang] = navigator.language.split('-');
      return lang;
    }

    const language = (get(searchObj, 'lang', '') || get(searchObj, 'language', ''));
    const parsedLang = Array.isArray(language) ? language[0] : language;
    return parsedLang.toLowerCase() || formatNavigatorLanguage() || 'es';
  }
);

export const selectTermsAndConditionsUrl = createSelector(
  getState,
  selectLanguage,
  (state, language) => {
    const lang = (language || '').toLowerCase();
    if (lang === 'pt') {
      return (
        get(state, 'serviceConfig.links.termsAndConditionsUrlBr') ||
        get(state, 'serviceConfig.TBX_TERMS_AND_CONDITIONS_URL_BR') ||
        ''
      );
    }
    return (
      get(state, 'serviceConfig.links.termsAndConditionsUrl') ||
      get(state, 'serviceConfig.TBX_TERMS_AND_CONDITIONS_URL') ||
      ''
    );
  }
);

export const selectClient = createSelector(
  selectPathname,
  (pathname) => {
    return pathname.split('/')[1];
  }
);

export const selectContentProvider = createSelector(
  selectSearchObj,
  (searchObj) => {
    return get(searchObj, 'cp', '') || new URLSearchParams(window.location.href).get('cp');
  }
);

export const selectOneTimeToken = createSelector(
  selectSearchObj,
  (searchObj) => {
    return get(searchObj, 'one_time_token', '');
  }
);

export const selectProductToUpgrade = createSelector(
  selectSearchObj,
  (searchObj) => {
    return get(searchObj, 'product', '');
  }
);

export const selectIdentityProvider = createSelector(
  selectClient,
  (idp) => idp
);

export const selectCancellationMethod = createSelector(
  getEntities,
  (entities) => get(entities, 'bootstrap.cancellationFlow')
);

export const selectClientTranslationURL = createSelector(
  selectClient,
  selectLanguage,
  (client, language) => `/clients/${client}/languages/${language}.json?_b=${process.env.REACT_APP_BUILDTIME}`
);

export const selectPublicKey = createSelector(
  selectLocation,
  location => {
    const search = location.search[0] === '?' ? location.search.substr(1) : location.search;
    const { key } = qs.parse(search);

    return key;
  }
);

export const selectServiceConfig = createSelector(
  getState,
  selectPublicKey,
  (state, publicKey) => ({
    ...state.serviceConfig,
    cloudpay: {
      ...state.serviceConfig,
      publicKey
    }
  })
);

export const selectUnityUrl = createSelector(
  getState,
  config => config && config.api.baseURL
);

export const selectCloudPayUrl = createSelector(
  getState,
  config => get(config, 'cloudpay.baseURL') || get(config, 'cloudpay.cloudpay.baseURL')
);

export const selectRedirectCloudPayUrl = createSelector(
  getState,
  config => {
    const url = get(config.serviceConfig, 'cloudpay.baseURL') || get(config.serviceConfig, 'cloudpay.cloudpay.baseURL')
    return `${url}/redirect/goBackToReturnUrl?cp={cp}&idp={idp}&return_url={returnUrl}&jwt={jwt}`;
  }
);

export const selectAffiliatesUrl = createSelector(
  getState,
  config => get(config, 'serviceConfig.affiliates.baseURL'),
);

export const selectDisneyCrmUrl = createSelector(
  getState,
  config => get(config, 'disneyCrm.baseURL') || get(config, 'disneyCrm.disneyCrm.baseURL')
);

export const selectStatusUrl = createSelector(
  getState,
  selectContentProvider,
  (config, cp) => {
    return config && config.pass.statusURL
      .replace('{CP}', cp);
  }
);

export const selectFaviconURL = createSelector(
  selectClient,
  (client) => `/clients/${client}/assets/images/favicon.ico`
);

export const selectLimitDevices = createSelector(
  selectSearchObj,
  (searchObj) => {
    return Number(get(searchObj, 'limit', 0));
  }
);

export const selectActualDevices = createSelector(
  selectSearchObj,
  (searchObj) => {
    return Number(get(searchObj, 'actual', 0));
  }
);

export const selectExpirationDevices = createSelector(
  selectSearchObj,
  (searchObj) => {
    return get(searchObj, 'expiration', new Date());
  }
);

export const selectCustomerId = createSelector(
  selectSearchObj,
  (searchObj) => {
    return get(searchObj, 'customerId', '');
  }
);

export const selectReturnUrl = createSelector(
  selectSearchObj,
  (searchObj) => {
    return get(searchObj, 'return_url', '');
  }
);

export const selectCancelUrl = createSelector(
  selectSearchObj,
  (searchObj) => {
    return get(searchObj, 'cancel_url', '');
  }
);